import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import { TYPES, PALETTE, typeOf, newFlowNodes, insertNode, isDisabled, summary, validate } from "./flowRules";
import { NodePanel } from "./nodePanels";

const errMsg = (e) => e?.response?.data?.message || e.message || "Something went wrong";

const FlowEditor = ({ show, flow, channels, ctx, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [nodes, setNodes] = useState([]);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [sel, setSel] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [saving, setSaving] = useState(false);
  const lastPush = useRef(0);
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  // scale the chain down so every step is visible (never above 100%)
  const fit = useCallback(() => {
    const w = wrapRef.current, c = canvasRef.current;
    if (!w || !c) return;
    const z = Math.min(1, (w.clientWidth - 8) / c.scrollWidth);
    setZoom(Math.max(0.5, Math.floor(z * 100) / 100));
  }, []);

  useEffect(() => {
    if (!show) return;
    setName(flow ? flow.name : "");
    setNodes(flow ? flow.nodes.map((n) => ({ ...n, config: { ...n.config } })) : newFlowNodes());
    setPast([]); setFuture([]); setSel(null); setZoom(1);
  }, [show, flow]);

  const issues = useMemo(() => validate(nodes), [nodes]);
  useEffect(() => { if (show) { const t = setTimeout(fit, 60); return () => clearTimeout(t); } }, [show, nodes.length, fit]);
  const issuesFor = (id) => issues.filter((i) => i.nodeId === id);
  const selected = nodes.find((n) => n.id === sel);

  // every edit goes through here; quick consecutive edits (typing) share one undo step
  const commit = (next, coalesce = false) => {
    const now = Date.now();
    if (!(coalesce && now - lastPush.current < 900)) setPast((p) => [...p.slice(-40), nodes]);
    lastPush.current = now;
    setFuture([]);
    setNodes(next);
  };
  const undo = () => { if (!past.length) return; setFuture((f) => [nodes, ...f]); setNodes(past[past.length - 1]); setPast(past.slice(0, -1)); };
  const redo = () => { if (!future.length) return; setPast((p) => [...p, nodes]); setNodes(future[0]); setFuture(future.slice(1)); };

  const add = (type) => { const next = insertNode(nodes, type); commit(next); setSel(next.find((n) => !nodes.some((o) => o.id === n.id)).id); };
  const remove = (id) => { commit(nodes.filter((n) => n.id !== id)); setSel(null); };
  const setConfig = (id, config) => commit(nodes.map((n) => (n.id === id ? { ...n, config } : n)), true);

  const save = async () => {
    setSaving(true);
    try {
      const body = { name: name.trim() || "Untitled flow", nodes };
      if (flow) await axiosInstance.put(`flows/${flow._id}`, body);
      else await axiosInstance.post("flows", body);
      onSaved();
      onClose();
    } catch (e) { swal("Could not save", errMsg(e), "error"); }
    setSaving(false);
  };

  const cancel = async () => {
    if (past.length) {
      const ok = await swal({ title: "Discard changes?", icon: "warning", buttons: ["Keep editing", "Discard"], dangerMode: true });
      if (!ok) return;
    }
    onClose();
  };

  if (!show) return null;
  const allIssues = issues.length;

  return (
    <div className="rf rf-editor-overlay">
      <div className="rf-editor">
        <div className="rf-editor-top">
          <div className="rf-head-icon" style={{ width: 38, height: 38, borderRadius: 10, background: "#E6F4EC", display: "flex", alignItems: "center", justifyContent: "center" }}>🔀</div>
          <input className="name" placeholder="Name this flow" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          <select className="form-control rf-mobile-add" value="" onChange={(e) => { if (e.target.value) add(e.target.value); }} aria-label="Add step">
            <option value="">+ Add step</option>
            {PALETTE.flatMap((g) => g.types).filter((t) => !isDisabled(nodes, t)).map((t) => <option key={t} value={t}>{TYPES[t].label}</option>)}
          </select>
          {allIssues > 0 && <span className="rf-chip red" style={{ fontSize: 13, padding: "5px 12px" }}>⚠ {allIssues} to fix</span>}
          <button className="btn-rf-o" onClick={cancel}>Cancel</button>
          <button className="btn-rf" disabled={allIssues > 0 || saving} onClick={save}>
            {saving ? <Spinner animation="border" size="sm" /> : flow ? "Save flow" : "Create flow"}
          </button>
        </div>

        <div className="rf-editor-body">
          <aside className="rf-palette">
            {PALETTE.map((g) => (
              <div key={g.title}>
                <h6>{g.title}</h6>
                {g.types.map((t) => (
                  <button key={t} className="rf-pal-item" disabled={isDisabled(nodes, t)} onClick={() => add(t)} title={TYPES[t].soon ? "Coming soon" : ""}>
                    <span className="rf-ico">{TYPES[t].icon}</span>
                    <span><div className="t">{TYPES[t].label}{TYPES[t].soon && <span className="rf-chip" style={{ marginLeft: 6 }}>Soon</span>}</div><div className="d">{TYPES[t].help}</div></span>
                  </button>
                ))}
              </div>
            ))}
          </aside>

          <div className="rf-canvas-col">
          <div className="rf-canvas-wrap" ref={wrapRef}>
            <div className="rf-canvas" ref={canvasRef} style={{ transform: `scale(${zoom})` }}>
              {nodes.map((n, i) => (
                <React.Fragment key={n.id}>
                  {i > 0 && <div className="rf-conn" />}
                  <button type="button" className={`rf-node ${sel === n.id ? "sel" : ""} ${issuesFor(n.id).length ? "bad" : ""}`} onClick={() => setSel(n.id)}>
                    {issuesFor(n.id).length > 0 && <span className="dot" />}
                    <div className="h"><span className="rf-ico" style={{ width: 24, height: 24, fontSize: 13 }}>{typeOf(n.type).icon}</span>{typeOf(n.type).label}</div>
                    {(() => { const s = summary(n, channels); const empty = /^(No |Choose)/.test(s); return <div className={`s ${empty ? "empty" : ""}`}>{s}</div>; })()}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
            <div className="rf-tools">
              <button onClick={() => setZoom((z) => Math.min(1.4, +(z + 0.1).toFixed(1)))} title="Zoom in">+</button>
              <button onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(1)))} title="Zoom out">−</button>
              <button onClick={fit} title="Fit">⤢</button>
              <button onClick={undo} disabled={!past.length} title="Undo">↶</button>
              <button onClick={redo} disabled={!future.length} title="Redo">↷</button>
            </div>
          </div>

          <aside className="rf-right">
            {selected ? (
              <>
                <div className="d-flex align-items-center mb-3" style={{ gap: 10 }}>
                  <span className="rf-ico" style={{ width: 40, height: 40, fontSize: 18 }}>{typeOf(selected.type).icon}</span>
                  <div><div style={{ fontWeight: 600 }}>{typeOf(selected.type).label}</div><div className="rf-help" style={{ marginTop: 0 }}>{typeOf(selected.type).help}</div></div>
                </div>
                {issuesFor(selected.id).map((i, k) => <div className="rf-alert red" key={k}>{i.message}</div>)}
                <NodePanel node={selected} ctx={{ ...ctx, channels, destChannelId: (nodes.find((n) => n.type === "postToChannel") || { config: {} }).config.channelId }} onChange={(config) => setConfig(selected.id, config)} />
                <div className="rf-panel-foot">
                  <button className="btn btn-link text-danger p-0" onClick={() => remove(selected.id)}>🗑 Delete step</button>
                  <button className="btn-rf-o" onClick={() => setSel(null)}>Done</button>
                </div>
              </>
            ) : (
              <>
                <h6 style={{ fontWeight: 600 }}>Steps</h6>
                <p className="rf-help mb-2">Posts move left to right through the chain. Click a step to set it up.</p>
                {issues.filter((i) => !i.nodeId).map((i, k) => <div className="rf-alert red" key={k}>{i.message}</div>)}
                {nodes.map((n) => {
                  const bad = issuesFor(n.id);
                  return (
                    <div className="rf-step" key={n.id} onClick={() => setSel(n.id)}>
                      <span className="rf-ico">{typeOf(n.type).icon}</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{typeOf(n.type).label}</div>
                        <div className="rf-help" style={{ marginTop: 0, color: bad.length ? "#E5484D" : undefined }}>{bad.length ? bad[0].message : summary(n, channels)}</div>
                      </div>
                      <span className="st">{bad.length ? "🔴" : "✅"}</span>
                    </div>
                  );
                })}
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default FlowEditor;
